import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
export const AUTH_TOKEN_KEY = 'token';
export const REQUEST_TIMEOUT_MS = 30000;
export const UPLOAD_TIMEOUT_MS = 120000;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: REQUEST_TIMEOUT_MS,
});

// Add auth token and language to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const language = localStorage.getItem('i18nextLng') || 'pt';

  config.headers = config.headers ?? {};
  config.headers['Accept-Language'] = language;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    if (status) {
      console.warn(`API error status=${status} url=${url}`, error.response?.data);
    }

    // If token is invalid/expired, clear it and send user to login (avoid loops on auth endpoints)
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/signup') || url.includes('/auth/google');
    if (status === 401 && !isAuthEndpoint) {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

export enum SubscriptionTier {
  FREE = 'FREE',
  PREMIUM = 'PREMIUM',
  PLATINUM = 'PLATINUM'
}

export interface TierLimits {
  tier: string;
  contracts_per_month: number;
  questions_per_contract_month: number;
  has_risk_statistics: boolean;
  has_risk_details: boolean;
}

export interface SubscriptionUsage {
  contracts_this_month: number;
  ai_questions_this_month: number;
}

export interface User {
  id: number;
  email: string;
  full_name: string | null;
  state?: string | null;
  city?: string | null;
  language?: string;  // User language preference: 'pt' or 'en'
  created_at: string;
  is_active: boolean;
  profile_photo_url?: string | null;
  subscription_tier: string;
  subscription_limits?: TierLimits;
  subscription_usage?: SubscriptionUsage;
}

export interface Document {
  id: number;
  auto_generated_name: string;
  document_type: string;
  user_role?: string;
  status: string;
  created_at: string;
  completed_at?: string;
  analysis?: any;
  summary_snippet?: string;
  qa_history?: DocumentQA[];
}

export interface DocumentQA {
  id: number;
  document_id?: number;
  user_id?: number;
  question: string;
  answer: string;
  created_at: string;
  rating?: number;
}

export interface Analysis {
  id: number;
  document_id: number;
  analysis_type: string;
  result: any;
  created_at: string;
}

export interface AnonymousSession {
  session_id: string;
  upload_id: string;
  status: string;
  original_filename: string;
  user_role: string;
  created_at: string;
  is_expired: boolean;
  full_analysis?: any;
  partial_analysis?: {
    contract_confirmed: boolean;
    contract_type: string;
    confidence: string;
    summary: {
      rental_value?: string;
      duration?: string;
      adjustment_index?: string;
    };
    parties?: any;
    risk_overview: {
      high: number;
      medium: number;
      low: number;
      total: number;
    };
    financial_impact_total_brl?: number | null;
    preview_risk?: {
      title: string;
      severity: string;
      potential_impact: string;
      recommendation: null;
      clause_text: null;
      clause_page: null;
      explanation: null;
      law_reference: null;
      legal_basis: null;
      financial_impact_brl: null;
    } | null;
    locked_content: {
      detailed_risks: boolean;
      recommendations: boolean;
      legal_references: boolean;
      qa_assistant: boolean;
    };
    error?: string;
    message?: string;
  };
}

// Auth APIs
export const authAPI = {
  signup: (data: { email: string; password: string; full_name: string; signup_source?: string; campaign_source?: string; campaign_session_id?: string; state?: string | null; city?: string | null; language?: string }) =>
    api.post('/auth/signup', data),

  login: (data: { email: string; password: string; campaign_source?: string; campaign_session_id?: string }) =>
    api.post('/auth/login', data),

  googleAuth: (data: { access_token: string }) =>
    api.post('/auth/google', data),

  me: () => api.get<User>('/auth/me'),

  questionsByContract: () => api.get('/auth/me/questions-by-contract'),

  updateProfile: (data: { full_name?: string | null; state?: string | null; city?: string | null; language?: string }) =>
    api.patch<User>('/auth/me', data),

  uploadProfilePhoto: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<User>('/auth/me/photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  logout: () => api.post('/auth/logout'),

  upgradeSubscription: (tier: string, campaign_source?: string, campaign_session_id?: string) =>
    api.post<User>('/auth/me/subscription/upgrade', { tier, campaign_source, campaign_session_id }),
};

// Document APIs
export const documentAPI = {
  upload: (file: File, role: 'tenant' | 'landlord', language?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_role', role);
    // Pass language for AI analysis output (default to browser language or 'pt')
    formData.append('language', language || localStorage.getItem('i18nextLng') || 'pt');
    const campaignSource = localStorage.getItem('campaign_source');
    const campaignSession = localStorage.getItem('tracking_session_id');
    if (campaignSource) formData.append('campaign_source', campaignSource);
    if (campaignSession) formData.append('campaign_session_id', campaignSession);
    return api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: UPLOAD_TIMEOUT_MS,
    });
  },

  list: () => api.get<{ documents: Document[]; total: number }>('/documents'),

  get: (id: string | number) => api.get<Document>(`/documents/${id}`),

  delete: (id: string | number) => api.delete(`/documents/${id}`),

  ask: (id: string | number, question: string, language?: string) =>
    api.post<DocumentQA>(`/documents/${id}/ask`, { question, language }),

  deleteQuestion: (id: string | number, questionId: string | number) =>
    api.delete(`/documents/${id}/questions/${questionId}`),

  rateAnswer: (
    documentId: string | number,
    payload: {
      rating: number;
      questionId?: string | number;
      wasCopied?: boolean;
      wasExported?: boolean;
      comment?: string;
    }
  ) =>
    api.post('/metrics/feedback', {
      document_id: documentId,
      feedback_type: 'qa_answer',
      rating: payload.rating,
      target_id: payload.questionId,
      was_copied: payload.wasCopied ?? false,
      was_exported: payload.wasExported ?? false,
      comment: payload.comment,
    }),

  download: (id: string | number) =>
    api.get(`/documents/${id}/download`, { responseType: 'blob' }),

  shareLink: (id: string | number) =>
    api.post<{ share_url: string; expires_at: string }>(`/documents/${id}/share`),

  rename: (id: string | number, name: string) =>
    api.patch<{ id: number; name: string }>(`/documents/${id}/name`, { name }),
};

// Anonymous APIs
export const anonymousAPI = {
  upload: (file: File, role: 'tenant' | 'landlord', language?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_role', role);
    // Pass language for AI analysis output (default to browser language or 'pt')
    formData.append('language', language || localStorage.getItem('i18nextLng') || 'pt');
    const campaignSource = localStorage.getItem('campaign_source');
    const campaignSession = localStorage.getItem('tracking_session_id');
    if (campaignSource) formData.append('campaign_source', campaignSource);
    if (campaignSession) formData.append('campaign_session_id', campaignSession);
    return api.post('/anonymous/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: UPLOAD_TIMEOUT_MS,
    });
  },

  getResults: (sessionId: string) =>
    api.get<AnonymousSession>(`/anonymous/results/${sessionId}`),

  migrate: (sessionId: string) =>
    api.post('/anonymous/migrate', { session_id: sessionId }),
};

export const trackingAPI = {
  logCtaClick: (payload: { cta_id: string; page?: string; location?: string; session_id?: string; language?: string }) =>
    api.post('/tracking/cta-click', payload),
  trackPageExit: (payload: {
    page: string;
    session_id?: string;
    campaign_source?: string;
    time_on_page_seconds: number;
    scroll_depth_percent: number;
    last_section_reached: string;
    sections_viewed: string[];
    exit_trigger: 'beforeunload' | 'visibilitychange' | 'navigation';
  }) => api.post('/tracking/page-exit', payload),
  trackUploadFunnel: (payload: {
    step: 'started' | 'role_selected' | 'file_selected' | 'upload_completed';
    session_id?: string;
    campaign_source?: string;
    role?: 'tenant' | 'landlord';
    button_location?: string;
  }) => api.post('/tracking/upload-funnel', payload),
};

// Notification Center APIs
export interface InAppNotification {
  id: number;
  user_id: number;
  document_id?: number;
  title: string;
  body?: string;
  deep_link?: string;
  is_read: boolean;
  created_at: string;
  read_at?: string;
}

export interface NotificationListResponse {
  notifications: InAppNotification[];
  total: number;
  unread_count: number;
}

export interface UnreadCountResponse {
  unread_count: number;
  badge_text: string;
}

export const notificationAPI = {
  list: (unreadOnly: boolean = false, limit: number = 50, offset: number = 0) =>
    api.get<NotificationListResponse>('/notifications', {
      params: { unread_only: unreadOnly, limit, offset }
    }),

  getUnreadCount: () =>
    api.get<UnreadCountResponse>('/notifications/unread-count'),

  markAsRead: (notificationId: number) =>
    api.post(`/notifications/${notificationId}/mark-read`),

  markMultipleAsRead: (notificationIds: number[]) =>
    api.post('/notifications/mark-read-bulk', { notification_ids: notificationIds }),

  markAllAsRead: () =>
    api.post('/notifications/mark-all-read'),

  delete: (notificationId: number) =>
    api.delete(`/notifications/${notificationId}`),
};

// Notification Settings APIs
export interface NotificationSettings {
  id: number;
  document_id: number;
  notifications_enabled: boolean;
  enabled_channels: string[];
}

export interface EventReminder {
  id: number;
  event_id: number;
  days_before: number | null;
  time_of_day: string;
  is_enabled: boolean;
  is_default: boolean;
}

export interface ContractEvent {
  id: number;
  document_id: number;
  event_type: string;
  event_date: string | null;
  event_day: number | null;
  recurrence: string | null;
  confidence: string;
  is_enabled: boolean;
  custom_label: string | null;
  description: string | null;
  reminders: EventReminder[];
}

export const notificationSettingsAPI = {
  getSettings: (documentId: number) =>
    api.get<NotificationSettings>(`/documents/${documentId}/notifications/settings`),

  updateSettings: (settings: Partial<NotificationSettings> & { document_id: number }) =>
    api.patch<NotificationSettings>(`/documents/${settings.document_id}/notifications/settings`, settings),
};

export const notificationEventsAPI = {
  getEvents: (documentId: number) =>
    api.get<ContractEvent[]>(`/documents/${documentId}/notifications/events`),

  regenerateEvents: (documentId: number, force: boolean = false) =>
    api.post<ContractEvent[]>(`/documents/${documentId}/notifications/events/regenerate`, { force }),

  updateEvent: (documentId: number, eventId: number, updates: Partial<ContractEvent>) =>
    api.patch<ContractEvent>(`/documents/${documentId}/notifications/events/${eventId}`, updates),

  sendTestNotification: (documentId: number, eventId: number) =>
    api.post(`/documents/${documentId}/notifications/events/${eventId}/send-test`),
};

export const notificationRemindersAPI = {
  addReminder: (documentId: number, eventId: number, reminder: { days_before: number; time_of_day: string }) =>
    api.post<EventReminder>(`/documents/${documentId}/notifications/events/${eventId}/reminders`, reminder),

  updateReminder: (documentId: number, reminderId: number, updates: Partial<EventReminder>) =>
    api.patch<EventReminder>(`/documents/${documentId}/notifications/reminders/${reminderId}`, updates),

  deleteReminder: (documentId: number, reminderId: number) =>
    api.delete(`/documents/${documentId}/notifications/reminders/${reminderId}`),
};

export const campaignAPI = {
  trackVisit: (payload: { source: string; landing_page?: string; session_id?: string; user_id?: number }) =>
    api.post('/campaigns/visit', payload),
};

export default api;
